/*
 * sku-search.js — internal SKU search for the storefront.
 *
 * Native search on this store indexes only the product title; tags, variant
 * SKU, and the customer-facing "SKU:" metafield (custom.sku) are NOT
 * searchable, and metafields can never be added to Shopify's index. So this
 * does its own lookup: it downloads a SKU -> {handle,title} map once
 * (templates/collection.sku-lookup.liquid), caches it, and:
 *
 *   1) As you type a SKU-ish query, shows matching products in a dropdown
 *      under the search box (partial / prefix match, case-insensitive,
 *      prefix optional). Normal word searches are left to Dawn's native
 *      predictive dropdown untouched.
 *   2) Submitting an exact SKU (or pressing Enter on a highlighted result)
 *      jumps straight to the product.
 *   3) Landing on /search?q=SKU directly redirects to the product.
 *
 * IMPORTANT: never fetch the map with `Accept: application/json` — Shopify
 * intercepts that on collection URLs and returns its own collection JSON,
 * bypassing our Liquid view.
 */
(function () {
  'use strict';

  var MAP_URL = '/collections/all?view=sku-lookup';
  var CACHE_KEY = 'pb_sku_map_v5'; // bump suffix to bust every visitor's cache
  var TTL_MS = 6 * 60 * 60 * 1000;
  var MAX_PAGES = 200;
  var CONCURRENCY = 6;
  var MAX_RESULTS = 6;

  var dataPromise = null;

  // Strict: used for submit/redirect. 3+ digits, optional 1-4 letter prefix.
  function looksLikeSku(q) {
    q = (q || '').trim();
    if (q.length < 5 || /\s/.test(q)) return false;
    return /^[A-Za-z]{0,4}\d{3,}[\w\-.\/]*$/.test(q);
  }
  // Loose: used to decide whether to show the as-you-type dropdown. Any
  // no-space, alphanumeric-ish token of 3+ chars that contains a digit.
  function skuish(q) {
    q = (q || '').trim();
    if (q.length < 3 || /\s/.test(q)) return false;
    return /\d/.test(q) && /^[A-Za-z0-9\-_.\/]+$/.test(q);
  }
  function normalize(s) { return String(s || '').trim().toUpperCase(); }
  function stripPrefix(s) { return s.replace(/^[A-Z]+/, ''); }
  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Some products have broken/missing featured images (404). Drop the src on
  // error so the styled gray placeholder shows instead of a broken-image icon.
  function attachImgFallback(root) {
    Array.prototype.forEach.call(root.querySelectorAll('img'), function (im) {
      function fb() { im.removeAttribute('src'); }
      im.addEventListener('error', fb);
      if (im.complete && im.naturalWidth === 0 && im.getAttribute('src')) fb();
    });
  }

  // ---------- data (download + cache) ----------
  function loadFromCache() {
    try {
      var o = JSON.parse(localStorage.getItem(CACHE_KEY));
      if (!o || !o.t || (Date.now() - o.t) > TTL_MS) return null;
      return o.d; // the product list
    } catch (e) { return null; }
  }
  function saveToCache(list) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: list })); } catch (e) {}
  }

  function fetchPage(page) {
    return fetch(MAP_URL + '&page=' + page)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // Each product becomes { h, t, keys:[exactSku, parentSku, cores...] } where
  // keys are upper-cased and include prefix-stripped numeric cores.
  function buildList() {
    var list = [];
    var ok = true;
    function fold(items) {
      items.forEach(function (it) {
        if (!it.h) return;
        var s = normalize(it.s), p = normalize(it.p);
        var keys = [];
        [s, p].forEach(function (k) {
          if (!k) return;
          keys.push(k);
          var core = stripPrefix(k);
          if (core && core !== k && keys.indexOf(core) === -1) keys.push(core);
        });
        if (keys.length) list.push({ h: it.h, t: it.t || it.h, i: it.i || '', pr: it.pr || '', keys: keys });
      });
    }
    return fetchPage(1).then(function (first) {
      if (!first || !first.items) return { list: list, ok: false };
      fold(first.items);
      var total = Math.min(first.pages || 1, MAX_PAGES);
      var queue = [];
      for (var p = 2; p <= total; p++) queue.push(p);
      var qi = 0;
      function worker() {
        if (qi >= queue.length) return Promise.resolve();
        var page = queue[qi++];
        return fetchPage(page).then(function (d) {
          if (!d || !d.items) ok = false; else fold(d.items);
          return worker();
        });
      }
      var ws = [];
      for (var w = 0; w < CONCURRENCY; w++) ws.push(worker());
      return Promise.all(ws).then(function () { return { list: list, ok: ok }; });
    });
  }

  // exact-key -> product index, derived from the list (kept out of the cache
  // to avoid duplicating every product per key).
  function buildIndex(list) {
    var byKey = {};
    list.forEach(function (p) { p.keys.forEach(function (k) { if (!(k in byKey)) byKey[k] = p; }); });
    list.forEach(function (p) { if (p.keys[0]) byKey[p.keys[0]] = p; }); // exact SKU wins
    return byKey;
  }

  function getData() {
    if (dataPromise) return dataPromise;
    var cached = loadFromCache();
    if (cached) {
      dataPromise = Promise.resolve({ list: cached, byKey: buildIndex(cached) });
      return dataPromise;
    }
    dataPromise = buildList().then(function (res) {
      if (res.ok && res.list.length) saveToCache(res.list);
      else dataPromise = null; // don't cache a failed/empty build; retry later
      return { list: res.list, byKey: buildIndex(res.list) };
    });
    return dataPromise;
  }

  function handleForSku(q) {
    return getData().then(function (d) {
      var n = normalize(q);
      var prod = d.byKey[n] || d.byKey[stripPrefix(n)];
      return prod ? prod.h : null;
    });
  }

  // Partial match for the dropdown: prefix matches first, then substring.
  function searchPartial(q, limit) {
    return getData().then(function (d) {
      var n = normalize(q);
      if (!n) return [];
      var pre = [], sub = [], seen = {};
      for (var i = 0; i < d.list.length; i++) {
        var p = d.list[i];
        if (seen[p.h]) continue;
        var hit = 0; // 0 none, 1 substring, 2 prefix
        for (var j = 0; j < p.keys.length; j++) {
          var idx = p.keys[j].indexOf(n);
          if (idx === 0) { hit = 2; break; }
          if (idx > 0) hit = 1;
        }
        if (hit) {
          seen[p.h] = 1;
          (hit === 2 ? pre : sub).push({ h: p.h, t: p.t, i: p.i, pr: p.pr, sku: p.keys[0] });
        }
      }
      return pre.concat(sub).slice(0, limit || MAX_RESULTS);
    });
  }

  // ---------- dropdown UI ----------
  var dd = null, ddActive = -1, currentInput = null, nativePanel = null, repo = null;

  function injectCSS() {
    if (document.getElementById('pb-skud-css')) return;
    var s = document.createElement('style');
    s.id = 'pb-skud-css';
    s.textContent =
      '.pb-skud{position:fixed;z-index:100000;background:#fff;border:1px solid #e3e3e3;' +
      'box-shadow:0 10px 28px rgba(0,0,0,.14);max-height:360px;overflow:auto;font-family:inherit}' +
      '.pb-skud__item{display:flex;gap:10px;align-items:center;padding:8px 12px;cursor:pointer;' +
      'text-decoration:none;color:#222;border-bottom:1px solid #f1f1f1}' +
      '.pb-skud__item:last-child{border-bottom:0}' +
      '.pb-skud__item--active,.pb-skud__item:hover{background:#f6f6f6}' +
      '.pb-skud__thumb{flex:0 0 40px;width:40px;height:52px;object-fit:cover;' +
      'background:#f2f2f2;border-radius:2px;display:block}' +
      '.pb-skud__txt{min-width:0}' +
      '.pb-skud__t{display:block;font-size:13px;line-height:1.35;white-space:nowrap;' +
      'overflow:hidden;text-overflow:ellipsis}' +
      '.pb-skud__sku{display:block;font-size:11px;letter-spacing:.02em;color:#8a8a8a;margin-top:2px}' +
      // results-page cards (injected into the native search grid)
      '.pb-skur__card{display:block;text-decoration:none;color:inherit}' +
      '.pb-skur__img{display:block;width:100%;aspect-ratio:2/3;object-fit:cover;background:#f2f2f2;margin-bottom:8px}' +
      '.pb-skur__t{font-size:14px;line-height:1.35}' +
      '.pb-skur__sku{font-size:12px;color:#8a8a8a;margin-top:3px}' +
      '.pb-skur__price{font-size:14px;margin-top:3px}';
    document.head.appendChild(s);
  }

  function ensureDD() {
    if (dd) return dd;
    injectCSS();
    dd = document.createElement('div');
    dd.className = 'pb-skud';
    dd.style.display = 'none';
    dd.setAttribute('role', 'listbox');
    // mousedown inside the panel must not blur the input before the click lands
    dd.addEventListener('mousedown', function (e) { e.preventDefault(); });
    document.body.appendChild(dd);
    return dd;
  }

  function positionDD(input) {
    var r = input.getBoundingClientRect();
    dd.style.left = r.left + 'px';
    dd.style.top = (r.bottom + 4) + 'px';
    dd.style.width = r.width + 'px';
  }
  function addReposition(input) {
    repo = function () { positionDD(input); };
    window.addEventListener('scroll', repo, true);
    window.addEventListener('resize', repo);
  }
  function removeReposition() {
    if (!repo) return;
    window.removeEventListener('scroll', repo, true);
    window.removeEventListener('resize', repo);
    repo = null;
  }

  function closeDD() {
    if (dd) dd.style.display = 'none';
    ddActive = -1;
    if (nativePanel) { nativePanel.style.display = ''; nativePanel = null; }
    removeReposition();
  }

  function openDD(input, items) {
    if (!items.length) { closeDD(); return; }
    ensureDD();
    currentInput = input;
    // hide Dawn's native predictive panel for this input while ours is shown
    var ps = input.closest('predictive-search');
    var np = ps && ps.querySelector('[data-predictive-search]');
    if (np && np !== nativePanel) { np.style.display = 'none'; nativePanel = np; }
    ddActive = -1;
    dd.innerHTML = items.map(function (it) {
      var src = it.i ? it.i.replace(/width=\d+/, 'width=100') : '';
      var thumb = src
        ? '<img class="pb-skud__thumb" src="' + esc(src) + '" alt="" loading="lazy" width="40" height="52">'
        : '<span class="pb-skud__thumb"></span>';
      return '<a class="pb-skud__item" role="option" href="/products/' + encodeURIComponent(it.h) + '">' +
        thumb +
        '<span class="pb-skud__txt">' +
        '<span class="pb-skud__t">' + esc(it.t) + '</span>' +
        '<span class="pb-skud__sku">' + esc(it.sku) + (it.pr ? ' · ' + esc(it.pr) : '') + '</span></span></a>';
    }).join('');
    attachImgFallback(dd);
    positionDD(input);
    dd.style.display = '';
    addReposition(input);
  }

  function setActive(i) {
    var nodes = dd.querySelectorAll('.pb-skud__item');
    if (!nodes.length) return;
    if (i < 0) i = nodes.length - 1;
    if (i >= nodes.length) i = 0;
    if (ddActive >= 0 && nodes[ddActive]) nodes[ddActive].classList.remove('pb-skud__item--active');
    ddActive = i;
    nodes[i].classList.add('pb-skud__item--active');
    nodes[i].scrollIntoView({ block: 'nearest' });
  }

  function isOpen() { return dd && dd.style.display !== 'none'; }

  // ---------- wiring ----------
  // As-you-type
  var debTimer = null;
  document.addEventListener('input', function (e) {
    var input = e.target;
    if (!input || input.tagName !== 'INPUT' || input.name !== 'q') return;
    if (!skuish(input.value)) { closeDD(); return; }
    getData(); // warm the map
    clearTimeout(debTimer);
    debTimer = setTimeout(function () {
      if (document.activeElement !== input || !skuish(input.value)) { closeDD(); return; }
      searchPartial(input.value).then(function (items) {
        if (document.activeElement !== input || !skuish(input.value)) { closeDD(); return; }
        openDD(input, items);
      });
    }, 130);
  });

  // Keyboard nav within the active search input (capture: beat predictive-search)
  document.addEventListener('keydown', function (e) {
    if (!isOpen() || e.target !== currentInput) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(ddActive + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(ddActive - 1); }
    else if (e.key === 'Enter') {
      var nodes = dd.querySelectorAll('.pb-skud__item');
      if (ddActive >= 0 && nodes[ddActive]) {
        e.preventDefault(); e.stopPropagation();
        window.location.href = nodes[ddActive].getAttribute('href');
      }
    } else if (e.key === 'Escape') { closeDD(); }
  }, true);

  // Close on outside click / focus leaving the search
  document.addEventListener('click', function (e) {
    if (isOpen() && !dd.contains(e.target) && e.target !== currentInput) closeDD();
  });
  document.addEventListener('focusout', function (e) {
    if (!e.target || e.target.name !== 'q') return;
    setTimeout(function () {
      if (document.activeElement !== currentInput && (!dd || !dd.contains(document.activeElement))) closeDD();
    }, 120);
  });

  // Prefetch the map when a shopper focuses any search box
  document.addEventListener('focusin', function (e) {
    var t = e.target;
    if (t && t.tagName === 'INPUT' && t.name === 'q') getData();
  });

  // Submit an exact SKU -> jump to product (capture: before Dawn)
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if ((form.getAttribute('action') || '').indexOf('/search') === -1) return;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    var q = input.value;
    if (!looksLikeSku(q)) return;
    e.preventDefault();
    handleForSku(q).then(function (handle) {
      if (handle) window.location.href = '/products/' + handle;
      else form.submit();
    }).catch(function () { form.submit(); });
  }, true);

  // Native search returns nothing for a SKU, so populate the results grid
  // ourselves with the matched products (reuses the theme's responsive grid).
  function renderResultsPage(items, q) {
    var grid = document.querySelector('#product-grid ul.grid') || document.querySelector('ul.product-grid');
    if (!grid) return false;
    injectCSS();
    grid.setAttribute('role', 'list');
    grid.innerHTML = items.map(function (it) {
      var img = it.i
        ? '<img class="pb-skur__img" src="' + esc(it.i) + '" alt="" loading="lazy">'
        : '<span class="pb-skur__img"></span>';
      return '<li class="grid__item">' +
        '<a class="pb-skur__card" href="/products/' + encodeURIComponent(it.h) + '">' +
        img +
        '<div class="pb-skur__t">' + esc(it.t) + '</div>' +
        '<div class="pb-skur__sku">' + esc(it.sku) + '</div>' +
        (it.pr ? '<div class="pb-skur__price">' + esc(it.pr) + '</div>' : '') +
        '</a></li>';
    }).join('');
    attachImgFallback(grid);
    // clear the empty state + "no results" message
    var wrap = document.querySelector('.template-search');
    if (wrap) wrap.classList.remove('template-search--empty');
    var header = document.querySelector('.template-search__header');
    if (header) Array.prototype.forEach.call(header.querySelectorAll('p[role="status"]'), function (p) { p.remove(); });
    // update the result count
    var label = items.length + ' result' + (items.length === 1 ? '' : 's') + ' found for "' + q + '"';
    ['ProductCountDesktop', 'ProductCountMobile'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.textContent = label;
    });
    return true;
  }

  // On the search results page: an exact SKU jumps to the product; a SKU-ish
  // partial renders its matches into the grid (so the previews aren't lost).
  function checkResultsPage() {
    if (location.pathname.indexOf('/search') !== 0) return;
    var q = new URLSearchParams(location.search).get('q');
    if (!q || !skuish(q)) return;
    getData().then(function (d) {
      var exact = d.byKey[normalize(q)] || d.byKey[stripPrefix(normalize(q))];
      if (exact) { window.location.href = '/products/' + exact.h; return; }
      return searchPartial(q, 60).then(function (items) {
        if (items.length) renderResultsPage(items, q);
      });
    });
  }

  checkResultsPage();
})();
